import React, { useState, useEffect } from 'react';
import styles from './CollapsibleSection.module.css';

// Fired by SetupChecklist.js to force a specific section open (by its `id`
// prop) and scroll to it, e.g. from a "add a destination" checklist item.
export const EXPAND_SECTION_EVENT = 'admin-panel:expand-section';

/**
 * Reusable collapsible section component
 */
export interface CollapsibleSectionProps {
  /** Section title */
  title: React.ReactNode;
  /** Section content */
  children?: React.ReactNode;
  /** Whether section starts expanded */
  defaultExpanded?: boolean;
  /** Icon to show in header (emoji or unicode) */
  icon?: string;
  /** Unique ID for localStorage persistence */
  id?: string;
  /** Additional CSS classes */
  className?: string;
  /** Callback when section is toggled */
  onToggle?: (isExpanded: boolean) => void;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultExpanded = true,
  icon = '',
  id = '',
  className = '',
  onToggle = () => {},
}) => {
  // Use localStorage to persist expanded state if ID is provided
  const getInitialExpanded = (): boolean => {
    if (id) {
      try {
        const saved = localStorage.getItem(`collapsible-${id}`);
        if (saved !== null) {
          const parsed = JSON.parse(saved);
          // Only honor a genuine boolean; anything else falls back below.
          if (typeof parsed === 'boolean') {
            return parsed;
          }
        }
      } catch {
        // Corrupt/non-JSON value or localStorage unavailable (e.g. private
        // browsing) — fall back to defaultExpanded instead of throwing
        // during render.
      }
    }
    return defaultExpanded;
  };

  const [isExpanded, setIsExpanded] = useState(getInitialExpanded);

  // Save state to localStorage when it changes
  useEffect(() => {
    if (id) {
      try {
        localStorage.setItem(`collapsible-${id}`, JSON.stringify(isExpanded));
      } catch {
        // Ignore storage errors (e.g. quota exceeded / disabled) — the
        // in-memory state still drives this session.
      }
    }
  }, [isExpanded, id]);

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onToggle(newExpanded);
  };

  // Let an external trigger (SetupChecklist.js) force this section open by
  // id, independent of the click-driven handleToggle above.
  useEffect(() => {
    if (!id) return undefined;

    const handleExpandEvent = (event: Event): void => {
      const detail = (event as CustomEvent<{ id?: string }>).detail;
      if (detail?.id === id) {
        setIsExpanded(true);
      }
    };

    window.addEventListener(EXPAND_SECTION_EVENT, handleExpandEvent);
    return () =>
      window.removeEventListener(EXPAND_SECTION_EVENT, handleExpandEvent);
  }, [id]);

  return (
    <section
      id={id || undefined}
      className={`${styles['collapsible-section']} ${className} ${styles[isExpanded ? 'expanded' : 'collapsed']}`}
    >
      <div className={styles['collapsible-header']} onClick={handleToggle}>
        <h2>
          {icon && <span className={styles['section-icon']}>{icon}</span>}
          {title}
        </h2>
        <button
          type="button"
          className={styles['collapse-toggle']}
          aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          aria-expanded={isExpanded}
        >
          <span
            className={`${styles['toggle-icon']} ${styles[isExpanded ? 'expanded' : 'collapsed']}`}
          >
            ▼
          </span>
        </button>
      </div>

      <div
        className={`${styles['collapsible-content']} ${styles[isExpanded ? 'expanded' : 'collapsed']}`}
      >
        <div className={styles['content-inner']}>{children}</div>
      </div>
    </section>
  );
};

export default CollapsibleSection;
